import React, {
  FocusEventHandler,
} from 'react';
import Checkbox from '@mui/material/Checkbox';
import isNil from 'lodash/isNil';
import {
  createFilterOptions,
} from '@mui/material/Autocomplete';
import AutoCompletedInput from 'core/components/AutoComplete';
import Pill from 'core/components/Pill';
import ToolTip from 'core/components/Tooltip';
import {
  UserDTO,
} from 'dtos';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import clsx from 'clsx';
import useUserStatusOptions from 'pages/Dashboard/hooks/useUserStatusOptions';
import ProviderLookupListDropdown from 'views/EMR/components/ProviderLookup/Dropdown';

type Props = {
  id: string;
  options: UserDTO[];
  value: UserDTO[];
  onChange: (value: UserDTO[]) => void;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  assigneeUserId?: number;
  disablePortal?: boolean;
};

type SelectAllOption = {
  type: 'selectAll';
  label: string;
};

type CollaboratorOption = UserDTO | SelectAllOption;

const selectAllOption: SelectAllOption = {
  type: 'selectAll',
  label: 'Select All',
};
const filterOptions = createFilterOptions<CollaboratorOption>();

function isSelectAllOption(option: CollaboratorOption): option is SelectAllOption {
  return 'type' in option && option.type === 'selectAll';
}

function isUserOption(option: CollaboratorOption): option is UserDTO {
  return !isSelectAllOption(option);
}

type SelectedCollaboratorsProps = {
  collaborators: UserDTO[];
  disabled: boolean;
  assigneeUserId?: number;
  onChange: (value: UserDTO[]) => void;
};

function SelectedCollaborators({
  collaborators,
  disabled,
  assigneeUserId,
  onChange,
}: SelectedCollaboratorsProps) {
  const removeCollaborator = (userId?: string | null) => {
    onChange(collaborators.filter((user) => (
      user.userId === assigneeUserId || user.userId?.toString() !== userId
    )));
  };
  const [firstCollaborator, ...remainingCollaborators] = collaborators;

  return isNil(firstCollaborator) ? null : (
    <div className="flex min-w-0 items-center gap-1">
      <Pill
        id={firstCollaborator.userId?.toString()}
        label={getDisplayName(firstCollaborator.formattedNames)}
        onRemove={removeCollaborator}
        disabled={disabled || firstCollaborator.userId === assigneeUserId}
      />
      {remainingCollaborators.length > 0 && (
        <ToolTip
          title={(
            <div className="flex flex-col space-y-2 p-2">
              {remainingCollaborators.map((user) => (
                <Pill
                  key={user.userId}
                  id={user.userId?.toString()}
                  label={getDisplayName(user.formattedNames)}
                  onRemove={removeCollaborator}
                  disabled={disabled || user.userId === assigneeUserId}
                />
              ))}
            </div>
          )}
          isInteractive
        >
          <span className="pointer-events-auto cursor-pointer">
            <Pill label={`+${remainingCollaborators.length}`} />
          </span>
        </ToolTip>
      )}
    </div>
  );
}

export default function CollaboratorsAutoComplete({
  id,
  options,
  value,
  onChange,
  onBlur,
  className,
  placeholder = 'Select collaborators',
  disabled = false,
  loading = false,
  assigneeUserId,
  disablePortal,
}: Props) {
  const {
    visibleOptions,
    inactiveConfig,
  } = useUserStatusOptions(options);
  const selectableOptions = visibleOptions.filter((option) => option.userId !== assigneeUserId);
  const selectedAssignee = value.filter((option) => option.userId === assigneeUserId);
  const allSelected = selectableOptions.length > 0 && selectableOptions.every((option) => (
    value.some((selected) => selected.userId === option.userId)
  ));

  return (
    <AutoCompletedInput<CollaboratorOption>
      id={id}
      multiple
      openOnFocus
      includeInputInList
      disableCloseOnSelect
      filterSelectedOptions={false}
      className={clsx(className)}
      options={[selectAllOption, ...visibleOptions]}
      value={value}
      disabled={disabled}
      loading={loading}
      disablePortal={disablePortal}
      dropdownClassName="!overflow-hidden p-2"
      slotProps={{
        listbox: {
          component: ProviderLookupListDropdown,
          inactiveConfig,
        },
        popper: {
          className: 'populate-collaborators',
        },
      } as Object}
      placeholder={placeholder}
      onBlur={onBlur}
      getLabel={(option) => (
        isSelectAllOption(option) ? option.label : getDisplayName(option.formattedNames)
      )}
      getOptionDisabled={(option) => (
        isSelectAllOption(option)
          ? selectableOptions.length === 0
          : option.userId === assigneeUserId
      )}
      isOptionEqualToValue={(option, selected) => {
        if (isSelectAllOption(option) || isSelectAllOption(selected as CollaboratorOption)) {
          return isSelectAllOption(option)
            && isSelectAllOption(selected as CollaboratorOption);
        }

        return option.userId === (selected as UserDTO).userId;
      }}
      filterOptions={(availableOptions, state) => [
        selectAllOption,
        ...filterOptions(
          availableOptions.filter((option) => !isSelectAllOption(option)),
          state,
        ),
      ]}
      renderOption={(optionProps, option, { selected }) => {
        const checked = isSelectAllOption(option) ? allSelected : selected;

        return (
          <li
            {...optionProps}
            key={isSelectAllOption(option) ? option.type : option.userId}
            className={clsx('!bg-transparent !text-xs hover:!bg-gray-100', optionProps.className)}
          >
            <Checkbox
              size="small"
              checked={checked}
              className={clsx({ '!text-blue-600': checked })}
            />
            <span className={clsx({
              'text-gray-400': !isSelectAllOption(option) && option.isActive === false,
            })}
            >
              {isSelectAllOption(option)
                ? option.label
                : getDisplayName(option.formattedNames)}
            </span>
          </li>
        );
      }}
      renderValue={() => (
        <SelectedCollaborators
          collaborators={value}
          disabled={disabled}
          assigneeUserId={assigneeUserId}
          onChange={onChange}
        />
      )}
      onChange={(nextValue) => {
        const selectedOptions = Array.isArray(nextValue) ? nextValue : [];

        if (selectedOptions.some(isSelectAllOption)) {
          onChange(allSelected ? selectedAssignee : [...selectedAssignee, ...selectableOptions]);
        } else {
          onChange(selectedOptions.filter(isUserOption));
        }
      }}
    />
  );
}
